import type { ReactElement } from 'react'
import { Navigate, Outlet } from 'react-router-dom'

import { useSession } from '../../auth/useSession'
import { SideNav, Spinner } from '../../components'
import { visibleAdminNav } from '../../lib/adminNav'
import { HOME_PATH } from '../../lib/routes'
import styles from './AdminLayout.module.scss'

/**
 * The administration area: the sidebar and the screen chosen in it. The
 * sidebar lists only the groups whose section the session holds, and each
 * screen keeps its own `RequireSection` in the route table — this layout
 * decides what is OFFERED, never what is allowed (the API does that).
 */
export const AdminLayout = (): ReactElement => {
  const { hasSection } = useSession()
  const groups = visibleAdminNav(hasSection).map((group) => ({
    title: group.title,
    links: group.entries.map((entry) => ({ to: entry.path, label: entry.label })),
  }))

  return (
    <div className={styles['admin-layout']}>
      <aside>
        <SideNav label="Administración" groups={groups} />
      </aside>
      <div className={styles['admin-layout__content']}>
        <Outlet />
      </div>
    </div>
  )
}

/**
 * `/admin` itself: the first screen of the sidebar the session can see. With
 * none — the URL typed by somebody holding none of the three sections — home,
 * which every guard's bounce already treats as the safe landing.
 */
export const AdminIndex = (): ReactElement => {
  const { isLoading, hasSection } = useSession()

  if (isLoading) {
    return <Spinner size="page" label="Comprobando permisos…" />
  }

  const [first] = visibleAdminNav(hasSection).flatMap((group) => group.entries)
  return <Navigate to={first?.path ?? HOME_PATH} replace />
}
